import { IGif } from '@giphy/js-types'
import React, { useEffect, useState } from 'react'
import { channelGifs } from 'shared/api'
import Loader from 'shared/components/loaders/dots'
import { grid12 } from 'shared/util/grid'
import { tween } from 'shifty'
import styled from 'styled-components'
import Starter, { STARTER_HEIGHT, STARTER_WIDTH } from '../starter-item'
import Arrow from './arrow'

const CHANNEL_ID = 26187095
const MAX_ITEMS = 5
const ITEM_MARGIN = 10
const MAX_WIDTH = (STARTER_WIDTH + ITEM_MARGIN) * MAX_ITEMS - ITEM_MARGIN

const Container = styled.div`
    box-sizing: content-box;
    height: ${STARTER_HEIGHT}px;
    margin: 0 auto;
    max-width: ${MAX_WIDTH}px;
    overflow: hidden;
    padding: 2px 0;
    position: relative;
`

const Wrapper = styled.div`
    height: 100%;
    left: 50%;
    margin-left: ${-grid12 / 2}px;
    position: absolute;
    top: 2px;
    width: ${grid12}px;
`

const Inner = styled.div`
    left: 0;
    position: absolute;
    top: 0;
`

const Item = styled.div`
    left: 0;
    position: absolute;
    top: 0;
    user-select: none;

    img {
        object-fit: cover;
    }
`

const Next = styled(Arrow)`
    right: 0;
    transform: scale(-1);
`

const Previous = styled(Arrow)`
    left: 0;
`

const ZoomMakerDownloadCarousel = () => {
    const [gifs, setGifs] = useState<IGif[]>([])
    const [indexes, setIndexes] = useState<number[]>([])
    const [scroll, setScroll] = useState<number>(0)
    const [isAnimating, setIsAnimating] = useState<boolean>(false)
    const isReady = gifs.length > MAX_ITEMS

    useEffect(() => {
        const fetchGifs = async () => {
            const { results = [] } = await channelGifs(CHANNEL_ID, MAX_ITEMS)
            const gifIndexes = results.map((_, i) => i)
            // move last item to first position
            gifIndexes.unshift(gifIndexes.pop() || 0)
            setGifs(results)
            setIndexes(gifIndexes)
        }
        fetchGifs()
    }, [])

    const updateIndexes = (newIndexes: number[], isPrevious = false) => {
        setIndexes(newIndexes)
        setIsAnimating(true)
        tween({
            from: { x: (STARTER_WIDTH + ITEM_MARGIN) * (isPrevious ? -1 : 1) },
            to: { x: 0 },
            duration: 300,
            easing: 'easeOutQuart',
            step: ({ x }) => setScroll(x),
        }).then(() => {
            setIsAnimating(false)
        })
    }

    const onNextClick = () => {
        if (isAnimating) return
        const next = [...indexes]
        next.push(next.shift() || 0)
        updateIndexes(next)
    }

    const onPreviousClick = () => {
        if (isAnimating) return
        const previous = [...indexes]
        previous.unshift(previous.pop() || 0)
        updateIndexes(previous, true)
    }

    return (
        <Container>
            {!gifs.length ? (
                <Loader isAbsolute />
            ) : (
                <>
                    <Wrapper>
                        <Inner style={{ transform: `translate3d(${scroll}px, 0, 0)` }}>
                            {indexes.map((index, i) =>
                                i >= MAX_ITEMS ? null : (
                                    <Item
                                        key={index}
                                        style={{
                                            transform: `translate3d(${(i - (isReady ? 1 : 0)) *
                                                (STARTER_WIDTH + ITEM_MARGIN)}px, 0, 0)`,
                                        }}
                                    >
                                        <Starter gif={gifs[index]} downloadOnly />
                                    </Item>
                                )
                            )}
                        </Inner>
                    </Wrapper>
                    {isReady && (
                        <>
                            <Previous onClick={onPreviousClick} />
                            <Next onClick={onNextClick} />
                        </>
                    )}
                </>
            )}
        </Container>
    )
}

export default ZoomMakerDownloadCarousel
